import { FilterTypes } from "../../../../types";
import { addDaysToDate } from "../../../../utils/dates";
/**
 * Looker field that scopes results to the partner linked to the given user
 * email. Kept in one place so every Overview filter query shares a single
 * source of truth for the field name.
 */
export const OVERVIEW_VIEWER_EMAIL_FILTER = "explore_overview.flt_viewer_email";
/**
 * The partner logo lives in its own `viewer_partner_logo` explore, which carries
 * its own viewer-email field — it is NOT reachable from explore_overview, so it
 * cannot reuse OVERVIEW_VIEWER_EMAIL_FILTER above.
 */
export const PARTNER_LOGO_VIEWER_EMAIL_FILTER = "viewer_partner_logo.viewer_email";
/**
 * Lower environments (dev/stage) may append this marker to a user's email in the
 * DB. Prod (real DB) never does, so stripping it is a safe no-op there.
 */
const DISABLED_EMAIL_SUFFIX = "-disabled";
/**
 * Normalizes the user email before it is sent to Looker: trims whitespace and
 * removes a trailing "-disabled" marker that lower-environment DBs can append.
 * Returns undefined when there is no usable email. Callers MUST fail closed in
 * that case — do NOT issue the query, as an unscoped partner query would leak
 * every partner's data across tenants.
 * every partner's data across tenants. The Overview filter handlers enforce
 * this by throwing "User email is required".
 */
export const normalizeUserEmail = (userEmail) => {
    const trimmed = userEmail?.trim();
    if (!trimmed) {
        return undefined;
    }
    const withoutSuffix = trimmed.toLowerCase().endsWith(DISABLED_EMAIL_SUFFIX)
        ? trimmed.slice(0, -DISABLED_EMAIL_SUFFIX.length)
        : trimmed;
    // Stripping the suffix can leave an empty string (e.g. input was just
    // "-disabled"); honor the documented contract by returning undefined so
    // callers still fail closed rather than seeing a falsy-but-defined email.
    return withoutSuffix || undefined;
};
/** Maps each metric to its real Looker measure in `explore_overview`. The
 *  breakdown query fetches all of these and the handler picks the selected
 *  one, so switching metrics needs no re-query change. */
export const OVERVIEW_BREAKDOWN_METRIC_FIELDS = {
    impressions: "explore_overview.total_impressions",
    clicks: "explore_overview.total_clicks",
    conversions: "explore_overview.total_conversions",
    completions: "explore_overview.total_video_completes"
};
/** All metric measures the breakdown query requests (per the Looker spec). */
export const OVERVIEW_BREAKDOWN_METRIC_FIELD_LIST = Object.values(OVERVIEW_BREAKDOWN_METRIC_FIELDS);
/** Single source of truth mapping each breakdown dimension to its Looker query
 *  shape. Adding an entry is all that is needed to enable a new dimension. */
export const OVERVIEW_BREAKDOWN_DIMENSION_FIELDS = {
    deviceType: { field: "explore_overview.device_type_grouped" },
    daypart: {
        field: "dayparts.summary",
        sortField: "dayparts.summary",
        params: { "explore_overview.pmt_timeframe": "hourly" }
    },
    lineItem: {
        field: "lineitem_name.line_item_name",
        // Top 5 line items; exclude rows with no line item name.
        limit: "5",
        extraFilters: { "lineitem_name.line_item_name": "-NULL" }
    },
    city: {
        field: "explore_overview.city",
        // Top 10 cities by the selected metric. A market can return thousands of
        // buckets and only the leaders are worth charting; 10 keeps the ranking
        // readable without turning the widget into a long scroll.
        //
        // Ten rows do NOT fit the 340px widget body (10 x 28px + 9 x 8px = 352px
        // of rows alone, before the axis and legend), so the chart scrolls inside
        // that body - the same way the breakdown tables already do. Only about
        // seven rows would fit outright, which is too short a ranking to be useful.
        //
        // Rows with no city are excluded rather than drawn as an empty bar.
        limit: "10",
        extraFilters: { "explore_overview.city": "-NULL" }
    },
    zipCode: {
        field: "explore_overview.postal",
        // Top 10 zip codes by the selected metric, matching City: the US alone has
        // ~40k ZCTAs, so only the leaders are worth charting. As with City, the
        // chart body scrolls at ten rows and blank postals are excluded rather than
        // drawn as an empty bar.
        limit: "10",
        extraFilters: { "explore_overview.postal": "-NULL" }
    }
};
/**
 * Row cap on the screenshots query. Search and pagination run client-side over
 * what this returns, so it is also the ceiling on what the search box can reach.
 */
export const OVERVIEW_SCREENSHOTS_QUERY_LIMIT = 500;
/**
 * Looker fields for the Screenshots widget. These live on the
 * `ad_verify_screenshots` view the explore joins in through campaigns, not on
 * explore_overview itself, which is why the prefix differs from the filters'.
 */
export const OVERVIEW_SCREENSHOT_FIELDS = {
    /** Publisher the ad rendered on, e.g. "usatoday.com". */
    publisher: "ad_verify_screenshots.url",
    /**
     * The LookML dimension prefixes the stored protocol-relative `file_name` with
     * "https:", but passes a non-URL through verbatim — hence the per-tile error
     * state in ScreenshotCard.
     */
    imageUrl: "ad_verify_screenshots.screenshot",
    /**
     * LookML formats this as '%F %T' — zero-padded, most-significant-first, which
     * is what lets the widget sort it lexicographically without parsing.
     */
    capturedAt: "ad_verify_screenshots.creation_time"
};
/** Coerces a number or numeric string to a finite number, else null. */
const toFiniteNumber = (value) => {
    const n = typeof value === "string" ? Number(value) : value;
    return typeof n === "number" && Number.isFinite(n) ? n : null;
};
/**
 * Parses the explore's `geofence_multipoints` JSON string into typed geofences.
 * Never throws: bad input (empty, non-string, malformed, wrong shape) yields [],
 * and a feature without finite center coordinates is dropped so one bad row
 * can't blank the layer.
 */
export const parseGeofenceMultipoints = (raw) => {
    if (typeof raw !== "string" || raw.trim() === "") {
        return [];
    }
    let parsed;
    try {
        parsed = JSON.parse(raw);
    }
    catch {
        return [];
    }
    const collection = parsed;
    if (collection?.type !== "FeatureCollection" || !Array.isArray(collection.features)) {
        return [];
    }
    return collection.features
        .map((feature) => {
        const props = feature?.properties ?? {};
        const centerLng = toFiniteNumber(props.center_point_longitude);
        const centerLat = toFiniteNumber(props.center_point_latitude);
        if (centerLng === null || centerLat === null) {
            return null;
        }
        // Drop out-of-range coordinates: Mapbox's Marker/fitBounds reject
        // latitudes outside [-90, 90], so one malformed feature could break the map.
        if (centerLat < -90 || centerLat > 90 || centerLng < -180 || centerLng > 180) {
            return null;
        }
        const color = typeof props.palette_color === "string" && props.palette_color !== ""
            ? props.palette_color
            : null;
        return {
            regionId: typeof props.region_id === "string" ? props.region_id : "",
            name: typeof props.name === "string" ? props.name : null,
            address: typeof props.address === "string" ? props.address : null,
            centerLng,
            centerLat,
            paletteColor: color,
            isRtgCenter: toFiniteNumber(props.is_rtg_center) === 1,
            thirdPartyRtg: props.third_party_rtg === true || props.third_party_rtg === "true",
            totalImpressions: toFiniteNumber(props.total_impression_sum),
            totalClicks: toFiniteNumber(props.total_click_sum),
            clickThroughRate: toFiniteNumber(props.click_through_rate)
        };
    })
        .filter((g) => g !== null);
};
/**
 * Formats a date-range filter as "yyyy-MM-dd to yyyy-MM-dd". The explore's
 * sql_always_where applies the end date as an exclusive bound
 * (date_date < date_end) with no implicit +1-day adjustment, so the picked
 * end date itself would otherwise be dropped — bump it by one day here so
 * the caller's inclusive end date is fully included.
 */
export const parseDateFilter = (filter) => {
    if (filter.type !== FilterTypes.BETWEEN || !Array.isArray(filter.value)) {
        throw new Error(`Filter ${String(filter.field)} must be a date range (BETWEEN) filter`);
    }
    const [startDate, endDate] = filter.value;
    if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
        throw new Error(`Filter ${String(filter.field)} must contain two dates when filtering for a date range`);
    }
    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };
    return `${formatDate(startDate)} to ${formatDate(addDaysToDate(endDate, 1))}`;
};
//# sourceMappingURL=index.js.map